import { Text, View } from "react-native";
import { useLocalSearchParams, usePathname } from "expo-router";
import LoadingSpinner from "ui/atoms/spinner/LoadingSpinner";
import { MedicationModel } from "app/services/api/types/medications/models/MedicationModel";
import { useMemo, useRef } from "react";
import GoBack from "ui/molecules/common/GoBack";
import KeyValueGrid from "ui/organisms/KeyValueGrid";
import { GridItem } from "ui/types/GridItem";
import { formatDate } from "ui/utils/dateUtils";
import { MedicationItem } from "app/features/medications/organisms/MedicationItem";
import { LargeText } from "ui/molecules/common/LargeText";
import { CreateEditMedicationModal } from "app/features/medications/modals/CreateEditMedicationModal";
import { DeleteMedicationModal } from "app/features/medications/modals/DeleteMedicationModal";
import { Button, ButtonText } from "ui/gluestack/button";
import {
  FrequencyAcronymMapping,
  FrequencyFriendlyNameMapping,
} from "app/services/api/types/medications/enums/FrequencyEnum";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionHeader,
  AccordionTitleText,
  AccordionIcon,
  AccordionContent,
} from "ui/gluestack/accordion";
import { ChevronDownIcon, ChevronUpIcon } from "ui/gluestack/icon";
import MedicationHistoryContent from "app/features/medications/organisms/MedicationHistoryContent";
import ReportCardContent from "app/features/medications/organisms/ReportCardContent";
import Pagination from "ui/molecules/pagination/Pagination";
import { useMedicationItemWithOffline } from "app/features/offline/hooks/useMedicationItemWithOffline";
import { useMedicationLogsWithOffline } from "app/features/offline/hooks/useMedicationLogsWithOffline";
import StatusIndicator from "ui/atoms/avatar/statusIndicator/StatusIndicator";
import {
  SignedStatusMapping,
  SignStatusEnums,
  StatusTitleMapping,
} from "app/services/api/types/medications/enums/SignStatusEnums";
import { PermissionCheck } from "app/features/permission/atoms/PermissionCheck";
import { PermissionsEnums } from "app/services/api/types/authorisation/enums/PermissionsEnums";
import { useHandleSignOffMedication } from "app/features/medications/hooks/useHandleSignOffMedication";
import {
  MedicationActionModal,
  MedicationActionModalRef,
} from "app/features/medications/modals/MedicationActionModal";
interface Props {
  isDiscontinued?: boolean;
}

export default function ViewMedicationItemTemplate({ isDiscontinued }: Props) {
  const pathname = usePathname();

  const { medicationId } = useLocalSearchParams();
  const {
    data: medicationReports,
    meta,
    isFetching: areReportsFetching,
    handlePageChange,
  } = useMedicationLogsWithOffline({ medicationId: Number(medicationId) });

  const { data: medicationItem, isFetching: isMedicationFetching } =
    useMedicationItemWithOffline({
      medicationId: Number(medicationId as string),
    });
  const { handleSignOffMedication } = useHandleSignOffMedication();

  const newPath = pathname.split("/").slice(0, -1).join("/");

  // Refs
  const ref =
    useRef<React.ComponentRef<typeof CreateEditMedicationModal>>(null);
  const discontinueRef =
    useRef<React.ComponentRef<typeof DeleteMedicationModal>>(null);
  const medicationActionRef = useRef<MedicationActionModalRef>(null);

  const startEdit = (medication: MedicationModel) => {
    ref.current?.show(medication);
  };

  const startDiscontinue = (medication: MedicationModel) => {
    discontinueRef.current?.show(medication);
  };

  const startMedicationAction = (
    action: "administer" | "selfAdminister" | "skipped",
    medicationId: number,
  ) => {
    medicationActionRef.current?.show(action, medicationId);
  };

  const medicationDetails = useMemo<GridItem[]>(() => {
    return [
      {
        label: "Diagnosis",
        value: medicationItem?.diagnosis || "N/A",
      },
      {
        label: "Dosage and unit of measure",
        value: medicationItem?.dosage || "N/A",
      },
      {
        label: "Form of medication",
        value: medicationItem?.form || "N/A",
      },
      {
        label: "Method of administration",
        value: medicationItem?.administration_method || "N/A",
      },
      {
        label: "Frequency of medication",
        value:
          (medicationItem
            ? `${FrequencyFriendlyNameMapping[medicationItem.frequency]} (${FrequencyAcronymMapping[medicationItem.frequency]})`
            : null) || "N/A",
      },
      {
        label: "Times",
        value: medicationItem?.times ? medicationItem.times.join(" & ") : "N/A",
      },
      ...(medicationItem?.day_of_week && medicationItem.day_of_week.length > 0
        ? [
            {
              label: "Days of week",
              value: medicationItem.day_of_week
                .map((day) => day.charAt(0).toUpperCase() + day.slice(1))
                .join(", "),
            },
          ]
        : []),
      ...(medicationItem?.day_of_month
        ? [
            {
              label: "Day of month",
              value: medicationItem.day_of_month.toString(),
            },
          ]
        : []),
      {
        label: "End of medication date",
        value: medicationItem?.end_date
          ? `${formatDate(medicationItem?.end_date)}`
          : "N/A",
      },
      {
        label: "Prescribed by",
        value: medicationItem?.prescribed_by || "N/A",
      },
      {
        label: "Signature",
        value: medicationItem?.sign_status ? (
          <StatusIndicator
            status={SignedStatusMapping[medicationItem?.sign_status]}
            statusTitle={StatusTitleMapping[medicationItem?.sign_status]}
          />
        ) : (
          "N/A"
        ),
      },
    ];
  }, [medicationItem]);

  return (
    <View className="resident-container pb-10">
      <CreateEditMedicationModal ref={ref} />
      <DeleteMedicationModal ref={discontinueRef} />
      <MedicationActionModal ref={medicationActionRef} />

      <View className="items-start mb-7">
        <GoBack path={newPath} />
      </View>
      {isMedicationFetching ? (
        <LoadingSpinner />
      ) : medicationItem ? (
        <View className="flex flex-col gap-y-2">
          <MedicationItem
            medication={medicationItem}
            isViewing
            startEditMedication={startEdit}
            startDiscontinueMedication={startDiscontinue}
            isDiscontinued={isDiscontinued}
          />
          <View className="rounded-xl border border-border-200 w-full px-9 py-10">
            <KeyValueGrid items={medicationDetails} />
            {medicationItem?.notes ? (
              <>
                <View className="border border-gray-200 w-full my-12" />
                <LargeText
                  title="Note"
                  className="md:w-10/12"
                  text={medicationItem?.notes}
                />
              </>
            ) : null}
            <View className="flex-row self-end gap-x-3 mt-12">
              {medicationItem?.sign_status ===
              SignStatusEnums.PENDING_SIGNATURE ? (
                <PermissionCheck
                  permission={PermissionsEnums.SIGN_OFF_MEDICATION}
                >
                  <View className="self-end">
                    <Button
                      variant="solid"
                      size="sm"
                      action="primary"
                      onPress={() => {
                        handleSignOffMedication({
                          id: Number(medicationId),
                        });
                      }}
                    >
                      <ButtonText>Sign Off</ButtonText>
                    </Button>
                  </View>
                </PermissionCheck>
              ) : null}
              {medicationItem && !isDiscontinued ? (
                <View className="flex-col sm:flex-row flex-1 gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    action="primary"
                    className="w-full sm:w-fit"
                    onPress={() =>
                      startMedicationAction("skipped", Number(medicationId))
                    }
                  >
                    <ButtonText>Skipped</ButtonText>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    action="primary"
                    className="w-full sm:w-fit"
                    onPress={() =>
                      startMedicationAction(
                        "selfAdminister",
                        Number(medicationId),
                      )
                    }
                  >
                    <ButtonText>Self Administered</ButtonText>
                  </Button>
                  <Button
                    action="primary"
                    size="sm"
                    variant="solid"
                    className="w-full sm:w-fit self-end"
                    onPress={() =>
                      startMedicationAction("administer", Number(medicationId))
                    }
                  >
                    <ButtonText>Administer</ButtonText>
                  </Button>
                </View>
              ) : null}
            </View>
          </View>
          <View className="rounded-xl border border-border-200 w-full p-6">
            <Accordion
              variant="filled"
              type="single"
              isCollapsible={true}
              isDisabled={false}
            >
              <AccordionItem value="a" className="bg-white">
                <AccordionHeader>
                  <AccordionTrigger>
                    {({ isExpanded }) => (
                      <>
                        {/* eslint-disable-next-line react-native/no-raw-text */}
                        <AccordionTitleText className="font-medium text-base">
                          Medication Log
                        </AccordionTitleText>
                        {isExpanded ? (
                          <AccordionIcon as={ChevronUpIcon} />
                        ) : (
                          <AccordionIcon as={ChevronDownIcon} />
                        )}
                      </>
                    )}
                  </AccordionTrigger>
                </AccordionHeader>
                <AccordionContent>
                  <View className="mb-2">
                    {areReportsFetching ? (
                      <LoadingSpinner />
                    ) : medicationReports?.length ? (
                      medicationReports.map((report) => (
                        <View
                          key={report.id}
                          className="mb-4 bg-background-100 p-5 rounded-lg last:mb-0"
                        >
                          <ReportCardContent medication={report} />
                        </View>
                      ))
                    ) : (
                      <Text>No logs available.</Text>
                    )}
                  </View>
                  <Pagination meta={meta} onPageChange={handlePageChange} />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </View>
          <View className="rounded-xl border border-border-200 w-full p-6">
            <Accordion
              variant="filled"
              type="single"
              isCollapsible={true}
              isDisabled={false}
            >
              <AccordionItem value="a" className="bg-white">
                <AccordionHeader>
                  <AccordionTrigger>
                    {({ isExpanded }) => (
                      <>
                        {/* eslint-disable-next-line react-native/no-raw-text */}
                        <AccordionTitleText className="font-medium text-base">
                          History
                        </AccordionTitleText>
                        {isExpanded ? (
                          <AccordionIcon as={ChevronUpIcon} />
                        ) : (
                          <AccordionIcon as={ChevronDownIcon} />
                        )}
                      </>
                    )}
                  </AccordionTrigger>
                </AccordionHeader>
                <AccordionContent>
                  <View>
                    {medicationItem ? (
                      <MedicationHistoryContent medication={medicationItem} />
                    ) : areReportsFetching ? (
                      <LoadingSpinner />
                    ) : (
                      <Text>No history available.</Text>
                    )}
                  </View>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </View>
        </View>
      ) : (
        <Text>Unable to get medication details at this time</Text>
      )}
    </View>
  );
}
